import {gsap, TimelineLite} from "gsap";
import {DumpsterFireSymbol, DumpsterFireSymbolStates, SYMBOL_NAME} from "../reels/DumpsterFireSymbol";
import {DumpsterFire} from "../DumpsterFire";
import {ISymbol} from "../../core/reel/simplereel/IReelData";
import {DFGameConfig} from "../DFGameConfig";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/utils/server/IData";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {DumpsterFireGameAssets} from "../DumpsterFireGameAssets";
import {DumpsterFireGameMode} from "../server/DumpsterFireGameMode";


export class DFWinView extends PIXI.Container {
    private _textHolder: PIXI.BitmapText[] = [];
    private _multiTextHolder: PIXI.Sprite[] = [];

    constructor() {
        super();
        this.name = "DFWinView";
    }

    public playWinAnimation(symbol: DumpsterFireSymbol, toSymbol?: string): TimelineLite {
        return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.WIN, toSymbol);
    }

    public playIdleAnimation(symbol: DumpsterFireSymbol): TimelineLite {
        return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.IDLE);
    }

    public playNoWinAnimation(symbol: DumpsterFireSymbol, toSymbol?: string): TimelineLite {
        return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.NO_WIN, toSymbol);
    }

    public playNormalAnimationForAll(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        for (let reelId: number = 0; reelId < DumpsterFire.model.data.reels.length; reelId++) {
            for (let rowId: number = 0; rowId < DumpsterFire.model.data.reels[reelId].length; rowId++) {
                let symbol: ISymbol = DumpsterFire.allReelsView.getSymbolAtPosition(reelId, rowId);
                let toSymbol: string = DumpsterFire.model.gameData.evaluatedReels[reelId][rowId];
                tl.add(this.playNormalAnimation(<DumpsterFireSymbol>symbol, toSymbol), 0);
            }
        }
        return tl;
    }

    public playGlowAnimation(symbol: DumpsterFireSymbol): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if ((DumpsterFire.model.data.mode.indexOf("NORMAL") > -1 && DumpsterFire.model.data.upgradedSymbols.length === 0) || (!(DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1) && DumpsterFire.model.data.nextMode === DumpsterFireGameMode.FREESPIN && DumpsterFire.model.data.upgradedSymbols.length > 0) && !(DumpsterFire.model.scatterCount > 5)) {
            return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.GLOW);
        }
        return tl
    }

    public playGlow_2_Animation(symbol: DumpsterFireSymbol): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if ((DumpsterFire.model.data.mode.indexOf("NORMAL") > -1 && DumpsterFire.model.data.upgradedSymbols.length === 0) || !(DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1) && DumpsterFire.model.data.nextMode === DumpsterFireGameMode.FREESPIN && DumpsterFire.model.data.upgradedSymbols.length > 0 && !(DumpsterFire.model.scatterCount >= 5)) {
            return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.GLOW_2);
        }
        return tl
    }

    public playNormalAnimation(symbol: DumpsterFireSymbol, toSymbol?: string): TimelineLite {
        return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.NORMAL, toSymbol);
    }

    public playLandingAnimation(symbol: DumpsterFireSymbol, toSymbol: string): TimelineLite {
        return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.LANDING, toSymbol);
    }

    public playTriggerAnimation(symbol: DumpsterFireSymbol): TimelineLite {
        return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.TRIGGER);
    }

    public playDisappearAnimation(symbol: DumpsterFireSymbol, toSymbol: string): TimelineLite {
        return (<DumpsterFireSymbol>symbol).getStateAnimation(DumpsterFireSymbolStates.DISAPPEAR, toSymbol);
    }

    private getStartPosition(symPosition: number[]): number[] {
        let reelId: number = symPosition[0],
            symId: number = symPosition[1];
        let posX: number = (DFGameConfig.symbolWidth * reelId) + 10,
            posY: number = (DFGameConfig.symbolHeight * symId) + 125;
        return [posX, posY];
    }

    private moveMultiAnim(betWayWinData: IBetWayWinData, xPos: number, yPos: number, width: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        for (let reelId: number = 0; reelId < DFGameConfig.totalReels; reelId++) {
            for (let rowId: number = 0; rowId < 5; rowId++) {
                if (betWayWinData.symbol === DumpsterFire.model.gameData.evaluatedReels[reelId][rowId] || DumpsterFire.model.gameData.evaluatedReels[reelId][rowId] === SYMBOL_NAME.WILD) {
                    let multipliedTextValue: number = DumpsterFire.model.data.positionMultipliers[reelId][rowId] / 2;
                    if (multipliedTextValue > 1) {
                        const multipliedText: PIXI.Sprite = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures(`multi${multipliedTextValue}`)[0]);
                        multipliedText.position.set(DFGameConfig.symbolWidth * reelId, DFGameConfig.symbolHeight * rowId + 50);
                        multipliedText.scale.set(0.9);
                        this.addChild(multipliedText);
                        tl.add(() => {
                            this._multiTextHolder.push(multipliedText);
                        }, 0);
                        tl.add(gsap.fromTo(multipliedText.position, {
                            x: multipliedText.position.x, y: multipliedText.position.y
                        }, {
                            x: xPos + width,
                            y: yPos - 40,
                            duration: 0.4
                        }), 0)
                    }
                }
            }
        }
        return tl;
    }

    public removeMovedMulti(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._multiTextHolder.forEach((item: PIXI.Sprite) => {
            tl.add(() => {
                this.removeChild(item)
            }, 0);
        })
        return tl
    }

    public playWinNumbers(betWayWinData: IBetWayWinData, skipped: boolean, symPosition: number[], winNumPlaySecondPosition: number[]): TimelineLite {
        let text: string = DumpsterFire.apiPlugin.currency.formatValue(MathHelper.roundToDecimals(betWayWinData.amount, 2));
        let multipliedTextValue: string = ''
        if (betWayWinData.multiplier > 1) {
            multipliedTextValue = ` X${betWayWinData.multiplier}`;
        }
        const winNumber: PIXI.BitmapText = new PIXI.BitmapText(text, {
            fontName: DumpsterFireGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 28
        });
        winNumber.scale.set(0.8);
        this.addChild(winNumber);
        winNumber.updateTransform();

        const startPositions: number[] = this.getStartPosition(symPosition);
        winNumber.position.set(startPositions[0] - 30, startPositions[1] + 30);

        const timeline: TimelineLite = new TimelineLite();
        let width: number = winNumber.width;
        timeline.add(gsap.to(winNumber, {duration: 0.1, alpha: 1}), 0);
        timeline.add(() => {
            this.moveMultiAnim(betWayWinData, startPositions[0], startPositions[1] + 30, width)
        }, 0.1)
        timeline.add([() => {
            winNumber.text = text + multipliedTextValue;
            this.removeMovedMulti();
        }], 0.4);

        if (winNumber.position.x < 20) {
            winNumber.position.x = 20
        }
        if (winNumber.position.x > 600) {
            winNumber.position.x -= 140
        }
        if (width > 234) {
            const scale: number = 234 / width;
            winNumber.scale.set(scale);
        }
        this._textHolder.push(winNumber);
        timeline.add(gsap.fromTo(winNumber.position, {
            duration: 1.2,
            y: winNumber.position.y
        }, {
            y: winNumber.position.y - 30
        }), "0.8");
        timeline.to(this, {duration: 0.2});
        return timeline;
    }

    private isValid(row: number, col: number, numRows: number, numCols: number): boolean {
        return row >= 0 && row < numRows && col >= 0 && col < numCols;
    }

    private findCluster(grid: boolean[][], row: number, col: number, visited: boolean[][]): [number, number][] {
        const stack: [number, number][] = [[row, col]];
        const clusterPositions: [number, number][] = [];
        const directions: [number, number][] = [
            [-1, 0],  //Up
            [1, 0],   //Down
            [0, -1],  //Left
            [0, 1],   //Right
            [-1, -1], //upLeft
            [-1, 1],  //upRight
            [1, -1],  //downLeft
            [1, 1],   //downRight
        ];
        visited[row][col] = true;

        while (stack.length > 0) {
            const [curRow, curCol] = stack.pop()!;
            clusterPositions.push([curRow, curCol]);

            // Explore neighbors
            for (const [dRow, dCol] of directions) {
                const newRow: number = curRow + dRow;
                const newCol: number = curCol + dCol;

                if (
                    this.isValid(newRow, newCol, grid.length, grid[0].length) &&
                    !visited[newRow][newCol] &&
                    grid[newRow][newCol]
                ) {
                    stack.push([newRow, newCol]);
                    visited[newRow][newCol] = true;
                }
            }
        }
        return clusterPositions;
    }

    public findLargestTrueCluster(grid: boolean[][]): [number, number] {
        const numRows: number = grid.length,
            numCols: number = grid[0].length,
            visited: boolean[][] = Array.from({length: numRows}, () => Array(numCols).fill(false));
        let largestCluster: [number, number][] = [];
        for (let row: number = 0; row < numRows; row++) {
            for (let col: number = 0; col < numCols; col++) {
                if (grid[row][col] && !visited[row][col] && DumpsterFire.model.data.reels[row][col] !== SYMBOL_NAME.WILD) {
                    const currentCluster: [number, number][] = this.findCluster(grid, row, col, visited);
                    if (currentCluster.length > largestCluster.length) {
                        largestCluster = currentCluster;
                    }
                }
            }
        }
        let centralClusterPos: [number, number] = largestCluster[Math.floor(largestCluster.length / 2)];
        if (DumpsterFire.model.data.reels[centralClusterPos[0]][centralClusterPos[1]] === SYMBOL_NAME.WILD) {
            centralClusterPos = largestCluster[Math.floor(largestCluster.length / 3)]
        }
        return centralClusterPos;
    }

    public finalWinNumber(betWayWinData: IBetWayWinData, idx: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let finalValue: number = betWayWinData.amount * betWayWinData.multiplier;
        tl.add(this._textHolder[idx].text = DumpsterFire.apiPlugin.currency.formatValue(MathHelper.roundToDecimals(finalValue, 2)), 0);
        if (betWayWinData.multiplier > 1) {
            this._textHolder[idx].anchor = new PIXI.Point(0, 0);
            this._textHolder[idx].position.x += 5;
        }
        if (this._textHolder[idx].width > 230) {
            tl.add(() => {
                const scale: number = 234 / this._textHolder[idx].width;
                this._textHolder[idx].scale.set(scale);
            }, 0);
        }
        return tl;
    }

    public removeWinNumbers(): void {
        const tl: TimelineLite = new TimelineLite();
        this._textHolder.forEach((text: PIXI.BitmapText) => {
            tl.to(text, {alpha: 0, duration: 0.2}, 0);
            tl.add(() => {
                this.removeChild(text);
                text.destroy();
            });
        });
        this._textHolder = [];
    }

    public xBombAnim(reelId: number, rowId: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let symbolName: string = DumpsterFire.model.data.reels[reelId][rowId];
        if (symbolName === SYMBOL_NAME.X_BOMB && DumpsterFire.model.findUpgradedSymbol("XB") && DumpsterFire.model.data.mode.indexOf("FREESPIN") > -1) {
            symbolName = SYMBOL_NAME.U_X_BOMB;
        }
        const winAnim: PIXI.spine.Spine = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset(symbolName));
        winAnim.visible = false;
        this.addChild(winAnim);
        winAnim.position.set(DFGameConfig.symbolWidth * reelId + 60, (DFGameConfig.symbolHeight * (rowId)) + 128)
        tl.add([() => {
            winAnim.visible = true;
        }, DumpsterFire.createTimelineForAnimation(winAnim, 0, "blast", false)]);
        tl.add(() => {
            this.removeChild(winAnim);
        });
        return tl;
    }

}